import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  Divider,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import PaymentIcon from "@mui/icons-material/Payment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../../components/navbar/navbar.component";
import { schemesV2Api, schemesV2Utils } from "../../services/schemesV2Api";
import { generalToastStyle } from "../../utils/toast.styles";

const SchemesV2Payment = () => {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [enrollment, setEnrollment] = useState(null);
  const [currentRates, setCurrentRates] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [isSubscriptionScheme, setIsSubscriptionScheme] = useState(false);
  const [isDirectPurchaseScheme, setIsDirectPurchaseScheme] = useState(false);
  const [is10MonthAccumulationScheme, setIs10MonthAccumulationScheme] = useState(false);
  const [paymentData, setPaymentData] = useState({
    installmentNumber: 1,
    amount: 0,
    paymentMethod: "UPI",
    paymentId: "",
    razorpayPaymentId: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Starting to fetch payment data for enrollment:', enrollmentId);
        setIsLoading(true);
        setError(null);

        const userId = localStorage.getItem('user_id');
        if (!userId) {
          toast.error("Please login to view payment details", generalToastStyle);
          navigate('/signin');
          return;
        }

        // Fetch enrollment details and current rates in parallel
        const [enrollmentResponse, ratesResponse] = await Promise.all([
          schemesV2Api.getEnrollmentDetails(enrollmentId, userId),
          schemesV2Api.getCurrentRates()
        ]);

        // Parse enrollment data from API response - find the specific enrollment by ID
        const allEnrollments = enrollmentResponse.data.activeSchemes || [];
        const enrollmentData = allEnrollments.find(env => env.id === enrollmentId) || 
                              enrollmentResponse.data.enrollment || 
                              enrollmentResponse.data;
        console.log('Enrollment response:', enrollmentResponse.data);
        console.log('All enrollments:', allEnrollments);
        console.log('Looking for enrollment ID:', enrollmentId);
        console.log('Found enrollment data:', enrollmentData);
        setEnrollment(enrollmentData);
        setCurrentRates(ratesResponse.data.data || ratesResponse.data);

        // Get installment number from URL query parameter or calculate next installment
        const urlParams = new URLSearchParams(location.search);
        const requestedInstallment = urlParams.get('installment');
        const periodsPaid = enrollmentData.plan_progress?.periods_paid || 0;
        const nextInstallmentNumber = requestedInstallment ? parseInt(requestedInstallment) : periodsPaid + 1;
        
        console.log('Payment calculation - periodsPaid:', periodsPaid, 'requestedInstallment:', requestedInstallment, 'nextInstallmentNumber:', nextInstallmentNumber);
        console.log('Enrollment data plan_progress:', enrollmentData.plan_progress);
        
        const paymentData = {
          installmentNumber: nextInstallmentNumber,
          amount: parseFloat(enrollmentData.instalment_amount || 0),
          paymentMethod: "UPI",
          paymentId: "",
          razorpayPaymentId: ""
        };
        
        console.log('Setting payment data:', paymentData);
        setPaymentData(paymentData);
        
        // Check if this is a subscription-based scheme
        const subscriptionId = enrollmentData.razorpaySubscriptionId || 
                             enrollmentData.subscription_id || 
                             enrollmentData.razorpay_subscription_id ||
                             enrollmentData.subscriptionId;
        
        const isMonthlyScheme = enrollmentData.scheme_type === 'monthly' || 
                               enrollmentData.scheme_id?.includes('month') ||
                               enrollmentData.scheme_name?.toLowerCase().includes('month') ||
                               enrollmentData.scheme_id?.includes('30-day') || // 30-day plans should use subscriptions
                               enrollmentData.tenure?.includes('month');
        
        const isSubscription = subscriptionId && isMonthlyScheme;
        console.log('Is subscription scheme:', isSubscription);
        setIsSubscriptionScheme(isSubscription);
        
        // Check if this is a direct purchase scheme (no gold accumulation)
        const isDirectPurchase = enrollmentData.scheme_id === '11-month-direct-purchase' || 
                                enrollmentData.scheme_name?.toLowerCase().includes('direct purchase') ||
                                enrollmentData.scheme_name?.toLowerCase().includes('no-making');
        console.log('Is direct purchase scheme:', isDirectPurchase);
        setIsDirectPurchaseScheme(isDirectPurchase);
        
        // Check if this is a 10-month accumulation scheme (only scheme that shows gold earned)
        const is10MonthAccumulation = enrollmentData.scheme_id === '10-month-accumulation';
        console.log('Is 10-month accumulation scheme:', is10MonthAccumulation);
        setIs10MonthAccumulationScheme(is10MonthAccumulation);
      } catch (err) {
        console.error('Error fetching payment data:', err);
        setError(err.message);
        toast.error(`Failed to load payment details: ${err.message}`, generalToastStyle);
      } finally {
        console.log('Finished loading, setting isLoading to false');
        setIsLoading(false);
      }
    };

    if (enrollmentId) {
      fetchData();
    }
  }, [enrollmentId, navigate, location.search]);

  const handlePayment = async () => {
    setIsProcessing(true);
    try {
      // Load Razorpay script first
      const loaded = await schemesV2Utils.loadRazorpayScript();
      if (!loaded) {
        toast.error("Razorpay SDK failed to load", generalToastStyle);
        setIsProcessing(false);
        return;
      }

      // Check if this enrollment has a subscription (for monthly schemes only)
      console.log('Enrollment data for payment:', enrollment);
      console.log('Subscription ID:', enrollment.razorpaySubscriptionId);
      console.log('Scheme ID:', enrollment.scheme_id);
      console.log('All enrollment keys:', Object.keys(enrollment));
      
      // Check for various possible subscription ID field names
      const subscriptionId = enrollment.razorpaySubscriptionId || 
                           enrollment.subscription_id || 
                           enrollment.razorpay_subscription_id ||
                           enrollment.subscriptionId;
      
      // Determine if this is a monthly scheme (should use subscriptions) or daily scheme (should use orders)
      const isMonthlyScheme = enrollment.scheme_type === 'monthly' || 
                             enrollment.scheme_id?.includes('month') ||
                             enrollment.scheme_name?.toLowerCase().includes('month') ||
                             enrollment.scheme_id?.includes('30-day') || // 30-day plans should use subscriptions
                             enrollment.tenure?.includes('month');
      
      console.log('Detected subscription ID:', subscriptionId);
      console.log('Scheme type:', enrollment.scheme_type);
      console.log('Is monthly scheme:', isMonthlyScheme);
      
      // Always allow manual payments for any installment
      console.log('Processing manual payment for installment:', paymentData.installmentNumber);
      await handleOrderPayment();
      
    } catch (error) {
      console.error('Payment initiation failed:', error);
      toast.error(`Payment failed: ${error.message}`, generalToastStyle);
      setIsProcessing(false);
    }
  };

  // Note: Subscription payments are now handled automatically by Razorpay
  // Manual payments should always use individual orders

  // Handle individual order payment (for any installment)
  const handleOrderPayment = async () => {
    try {
      // Create Razorpay order for any installment
      const orderData = {
        amount: paymentData.amount
      };

      const orderResponse = await schemesV2Api.createInstallmentOrder(
        parseInt(enrollmentId), 
        paymentData.installmentNumber, 
        orderData
      );
      const order = orderResponse.data;

      // Initialize Razorpay payment
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: order.amount,
        currency: order.currency,
        name: "SadāShrī Jewelkart",
        description: `Scheme Payment - Installment #${paymentData.installmentNumber}`,
        order_id: order.order_id,
        receipt: order.receipt,
        handler: async function (response) {
          try {
            // Validate Razorpay response
            schemesV2Utils.validateRazorpayResponse(response);

            // Verify payment with backend
            const verificationData = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              enrollment_id: parseInt(enrollmentId),
              installment_number: paymentData.installmentNumber
            };

            const verificationResponse = await schemesV2Api.verifyPayment(verificationData);
            
            // Store success data and show success page
            setSuccessData(verificationResponse.data);
            setPaymentSuccess(true);
            
            // Auto redirect after 3 seconds
            setTimeout(() => {
              navigate(`/schemes-v2/${enrollment.scheme_id}`);
            }, 3000);
            
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast.error(`Payment verification failed: ${error.message}`, generalToastStyle);
            setIsProcessing(false);
          }
        },
        prefill: {
          email: localStorage.getItem("email") || "",
          contact: localStorage.getItem("phone") || "",
        },
        theme: {
          color: "#A36E29",
        },
        modal: {
          ondismiss: function() {
            setIsProcessing(false);
          }
        }
      };

      const razorpay = schemesV2Utils.initializeRazorpayPayment(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Order payment failed:', error);
      toast.error(`Order payment failed: ${error.message}`, generalToastStyle);
      setIsProcessing(false);
    }
  };


  const getStatusChip = (status) => {
    const colors = {
      paid: { bg: "#e8f5e8", color: "#2e7d32" },
      next: { bg: "#fff3e0", color: "#f57c00" },
      pending: { bg: "#f5f5f5", color: "#616161" }
    };
    
    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        size="small"
        sx={{
          backgroundColor: colors[status]?.bg || colors.pending.bg,
          color: colors[status]?.color || colors.pending.color,
          fontWeight: 600,
          fontSize: "12px"
        }}
      />
    );
  };

  // Debug logging
  console.log('Payment component render - isLoading:', isLoading, 'error:', error, 'enrollment:', enrollment);
  console.log('Payment data:', paymentData);

  // Handle loading state
  if (isLoading) {
    console.log('Rendering loading state');
    return (
      <Container maxWidth="xl" disableGutters>
        <Navbar />
        <Box
          sx={{
            background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
            minHeight: "100vh",
            pt: { xs: 15, md: 12 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <CircularProgress 
            size={60} 
            sx={{ color: "#A36E29", mb: 2 }} 
          />
          <Typography variant="h6" sx={{ color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
            Loading payment details...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    console.log('Rendering error state');
    return (
      <Container maxWidth="xl" disableGutters>
        <Navbar />
        <Box
          sx={{
            background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
            minHeight: "100vh",
            pt: { xs: 15, md: 12 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <Typography variant="h6" sx={{ color: "#d32f2f", mb: 2 }}>
            Error loading payment details
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ backgroundColor: "#A36E29" }}
          >
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  // Handle payment success state
  if (paymentSuccess && successData) {
    console.log('Rendering payment success state');
    return (
      <Container maxWidth="xl" disableGutters>
        <Navbar />
        <Box
          sx={{
            background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
            minHeight: "100vh",
            pt: { xs: 15, md: 12 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <Container maxWidth="md">
            <Card sx={{ borderRadius: 3, overflow: "hidden", textAlign: "center" }}>
              <CardContent sx={{ p: 6 }}>
                {/* Success Icon */}
                <Box sx={{ mb: 4 }}>
                  <CheckCircleIcon 
                    sx={{ 
                      fontSize: 80, 
                      color: "#4caf50",
                      mb: 2
                    }} 
                  />
                </Box>

                {/* Success Message */}
                <Typography 
                  variant="h4" 
                  sx={{ 
                    color: "#4caf50", 
                    fontWeight: 700, 
                    mb: 2,
                    fontFamily: '"Roboto", sans-serif'
                  }}
                >
                  Payment Successful!
                </Typography>

                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: "#A36E29", 
                    mb: 4,
                    fontFamily: '"Roboto", sans-serif'
                  }}
                >
                  Your installment has been processed successfully
                </Typography>

                {/* Payment Details */}
                <Paper sx={{ p: 3, mb: 4, backgroundColor: "#f8f9fa" }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                    Payment Details
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                        Payment ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                        {successData.paymentId}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                        Amount Paid
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                        ₹{parseFloat(successData.amount).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                        Installment
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                        #{successData.installmentNumber}
                      </Typography>
                    </Grid>
                    {/* Gold Grams Earned - Only show for 10-month accumulation scheme */}
                    {is10MonthAccumulationScheme && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                          Gold Grams Earned
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                          {parseFloat(successData.goldGramsCredited).toFixed(4)} grams
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  {/* Updated Progress */}
                  {successData.updatedProgress && (
                    <Box sx={{ mt: 3, p: 2, backgroundColor: "rgba(76, 175, 80, 0.1)", borderRadius: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, fontFamily: '"Roboto", sans-serif' }}>
                        Updated Progress
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "#4caf50", fontFamily: '"Roboto", sans-serif' }}>
                        {successData.updatedProgress.percentage}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                        {successData.updatedProgress.periods_paid} of {successData.updatedProgress.total_periods} installments paid
                      </Typography>
                    </Box>
                  )}
                </Paper>

                {/* Next Payment Info */}
                {successData.nextPayment && (
                  <Alert severity="info" sx={{ mb: 4, textAlign: "left" }}>
                    <Typography variant="body2" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                      <strong>Next Payment:</strong> Installment #{successData.nextPayment.installment_number} due on {successData.nextPayment.due_date}
                    </Typography>
                  </Alert>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/schemes-v2/${enrollment.scheme_id}`)}
                    sx={{ 
                      borderColor: "#A36E29", 
                      color: "#A36E29",
                      fontFamily: '"Roboto", sans-serif',
                      "&:hover": {
                        borderColor: "#8b5a1f",
                        backgroundColor: "rgba(163, 110, 41, 0.1)"
                      }
                    }}
                  >
                    View Scheme Details
                  </Button>
                  
                  <Button
                    variant="contained"
                    onClick={() => navigate('/my-account/my-schemes')}
                    sx={{ 
                      backgroundColor: "#A36E29",
                      fontFamily: '"Roboto", sans-serif',
                      "&:hover": {
                        backgroundColor: "#8b5a1f"
                      }
                    }}
                  >
                    My Schemes
                  </Button>
                </Box>

                {/* Auto redirect countdown */}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "text.secondary", 
                    mt: 3,
                    fontFamily: '"Roboto", sans-serif'
                  }}
                >
                  Redirecting to scheme details in 3 seconds...
                </Typography>
              </CardContent>
            </Card>
          </Container>
        </Box>
      </Container>
    );
  }

  if (!enrollment) {
    console.log('No enrollment data, returning null');
    return null;
  }

  console.log('Rendering main payment page');

  return (
    <Container maxWidth="xl" disableGutters>
      <Navbar />
      
      <Box
        sx={{
          background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
          minHeight: "100vh",
          pt: { xs: 15, md: 12 },
          pb: 4
        }}
      >
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{
                color: "#A36E29",
                fontWeight: 700,
                mb: 1,
                fontFamily: '"Roboto", sans-serif'
              }}
            >
              Make Payment
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(163, 110, 41, 0.8)",
                fontFamily: '"Roboto", sans-serif'
              }}
            >
              Complete your installment #{paymentData.installmentNumber} for {enrollment.scheme_name || enrollment.scheme?.name || 'Your Scheme'}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Payment Form */}
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, fontFamily: '"Roboto", sans-serif' }}>
                    Payment Details
                  </Typography>

                  {/* Current Rates - Only show for 10-month accumulation scheme */}
                  {currentRates && is10MonthAccumulationScheme && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Typography variant="body2">
                        <strong>Current Gold Rate:</strong> ₹{currentRates.rates?.gold22k?.toLocaleString()} per gram (22K)
                      </Typography>
                    </Alert>
                  )}

                  {/* Payment Amount */}
                  <Paper sx={{ p: 3, mb: 3, backgroundColor: "#f8f9fa" }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                      Payment Amount
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                      ₹{paymentData.amount.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                      Installment #{paymentData.installmentNumber}
                    </Typography>
                  </Paper>

                  {/* Payment Method */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                      Payment Method
                    </Typography>
                    <Chip
                      icon={<PaymentIcon color="white" />}
                      label={paymentData.paymentMethod}
                      sx={{
                        backgroundColor: "#A36E29",
                        color: "white",
                        fontWeight: 600
                      }}
                    />
                  </Box>

                  {/* Gold Grams Calculation - Only show for 10-month accumulation scheme */}
                  {currentRates && is10MonthAccumulationScheme && (
                    <Paper sx={{ p: 3, mb: 3, backgroundColor: "rgba(163, 110, 41, 0.1)" }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                        Gold Grams Earned
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                        {schemesV2Utils.calculateGoldGrams(paymentData.amount, currentRates.rates?.gold22k).toFixed(4)} grams
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                        Based on current 22K gold rate
                      </Typography>
                    </Paper>
                  )}

                  {/* Scheme-specific Messages */}
                  {isDirectPurchaseScheme ? (
                    <Alert 
                      severity="info" 
                      sx={{ 
                        mb: 3,
                        backgroundColor: "rgba(163, 110, 41, 0.1)",
                        borderColor: "rgba(163, 110, 41, 0.3)",
                        '& .MuiAlert-icon': {
                          color: "#A36E29",
                        },
                        '& .MuiAlert-message': {
                          color: "#A36E29",
                        }
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, fontFamily: '"Roboto", sans-serif' }}>
                        Direct Purchase Plan
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                        This scheme provides 100% making charge waiver on your chosen jewelry. No gold accumulation - you pay for the jewelry directly with making charge benefits.
                      </Typography>
                    </Alert>
                  ) : null}

                  {/* Payment Button - Always show for all schemes */}
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handlePayment}
                    disabled={isProcessing}
                    sx={{
                      background: "linear-gradient(to right, #d4a76a, #A36E29)",
                      borderRadius: 2,
                      py: 2,
                      fontWeight: 600,
                      fontSize: "18px",
                      fontFamily: '"Roboto", sans-serif',
                      "&:hover": {
                        background: "linear-gradient(to right, #c18a3d, #8b5a1f)"
                      }
                    }}
                  >
                    {isProcessing ? (
                      <>
                        <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                        Processing Payment...
                      </>
                    ) : (
                      <>
                        <PaymentIcon sx={{ mr: 1 }} color="white" />
                        Pay ₹{paymentData.amount.toLocaleString()}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Enrollment Summary */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, fontFamily: '"Roboto", sans-serif' }}>
                    Enrollment Summary
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                      Scheme
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                      {enrollment.scheme_name || enrollment.scheme?.name || 'N/A'}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                      Progress
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                      {enrollment.plan_progress?.percentage || enrollment.progress?.completionPercentage || 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                      {enrollment.plan_progress?.periods_paid || enrollment.progress?.paidInstallments || 0} of {enrollment.plan_progress?.total_periods || enrollment.progress?.totalInstallments || 0} installments paid
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                      Total Invested
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                      ₹{parseFloat(enrollment.plan_progress?.total_paid || enrollment.progress?.totalInvested || 0).toLocaleString()}
                    </Typography>
                  </Box>

                  {/* Gold Grams Earned - Only show for 10-month accumulation scheme */}
                  {is10MonthAccumulationScheme && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                        Gold Grams Earned
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                        {parseFloat(enrollment.gold_grams_accumulated || enrollment.progress?.goldGramsEarned || 0).toFixed(4)} grams
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                      Completion Reward
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                      {enrollment.completion_reward || enrollment.scheme?.completion_reward || 'N/A'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Recent Payments */}
              <Card sx={{ mt: 3, borderRadius: 3, overflow: "hidden" }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, fontFamily: '"Roboto", sans-serif' }}>
                    Recent Payments
                  </Typography>
                  
                  {(enrollment.paymentHistory || enrollment.payment_history || []).slice(0, 3).map((payment, index) => (
                    <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < 2 ? "1px solid #eee" : "none" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                          Installment #{payment.installmentNumber || payment.installment_number || index + 1}
                        </Typography>
                        {getStatusChip(payment.status)}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                        ₹{parseFloat(payment.amount || payment.payment_amount || 0).toLocaleString()} • {payment.paymentDate ? schemesV2Utils.formatDate(payment.paymentDate) : payment.payment_date ? schemesV2Utils.formatDate(payment.payment_date) : 'N/A'}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Container>
  );
};

export default SchemesV2Payment;
